import { configureStore, createAsyncThunk, createReducer } from "@reduxjs/toolkit";
import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";
import { CdPlayerModule, initCdPlayerModule, cdLoadDisc, cdStartTrack, cdResume, cdPause } from "./CdPlayerModule";

const invalidPlayingIndex = -1;

export type AudioState = {
    value: string,
    trackStarted: boolean
    playing: boolean
    playingIndex: number
    trackNumberList : number[]
    trackNumberListVersion : number;
};

let initialState : AudioState = {
    value: "unknown",
    trackStarted: false,
    playing: false,
    playingIndex: invalidPlayingIndex,
    trackNumberList: [],
    trackNumberListVersion: 0,
};

export const init = createAsyncThunk<void>('init', 
    async () => {
        await initCdPlayerModule();
    }
);

export const loadDisc = createAsyncThunk<number[] | undefined, File>('loadDisc',
    async (file : File, thunkAPI) => {
        const fileName = file.name;
        let url = URL.createObjectURL(file);
        console.log(`loading ${url}...`);
        //await stopPsf();
        CdPlayerModule.discImageDevice.setFile(file);
        let trackList = await cdLoadDisc(fileName);
        return trackList;
    }
);

export const startTrack = createAsyncThunk<number, number>('startTrack',
    async (trackNumberIndex : number, thunkAPI) => {
        let state = thunkAPI.getState() as RootState;
        let trackNumber = state.player.trackNumberList[trackNumberIndex] as number;
        await cdStartTrack(trackNumber);
        return trackNumberIndex;
    }
);

export const play = createAsyncThunk<void, void>('play',
    async () => {
        await cdResume();
    }
);

export const pause = createAsyncThunk<void, void>('pause',
    async() => {
        await cdPause();
    }
);

const reducer = createReducer(initialState, (builder) => (
    builder
        .addCase(init.fulfilled, (state, action) => {
            console.log("init");
            state.value = "initialized";
            return state;
        })
        .addCase(loadDisc.fulfilled, (state, action) => {
            state.value = "loaded";
            state.playingIndex = invalidPlayingIndex;
            if(action.payload) {
                state.trackNumberList = action.payload;
            } else {
                state.trackNumberList = [];
            }
            state.trackNumberListVersion = state.trackNumberListVersion + 1;
            return state;
        })
        .addCase(loadDisc.rejected, (state, action) => {
            state.playingIndex = invalidPlayingIndex;
            state.trackNumberList = [];
            state.trackNumberListVersion = state.trackNumberListVersion + 1;
            state.value = `loading failed: ${action.error.message}`;
            return state;
        })
        .addCase(startTrack.fulfilled, (state, action) => {
            state.value = "track started";
            state.playingIndex = action.payload;
            state.trackStarted = true;
            state.playing = true;
            return state;
        })
        .addCase(startTrack.rejected, (state, action) => {
            state.value = `loading failed: ${action.error.message}`;
            return state;
        })
        .addCase(play.fulfilled, (state) => {
            state.value = "playing";
            state.playing = true;
            return state;
        })
        .addCase(pause.fulfilled, (state) => {
            state.value = "paused";
            state.playing = false;
            return state;
        })
));

export const store = configureStore({
    reducer: { player: reducer }
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
